import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { isIOS, isMobile } from 'react-device-detect';
import { Sheet } from 'react-modal-sheet';
import { useFilePicker } from 'use-file-picker';

import { EditFill } from '@peacecover/icon';
import { Button, InputBoxSingle } from '@peacecover/component';

import { useSuccessPhotoCardToast } from '../../presentation/template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import { useAddAndMergeUserProfile } from '../../domains/user/ui/hooks/useAddAndMergeUserProfile';
import { useAddUserProfile } from '../../domains/user/ui/hooks/useAddUserProfile';

import { t } from 'i18next';
import { first } from 'lodash';
import { isNativeIOS } from '../../utils/NativeUtil';
import { hasBatchim } from '../../utils/TextUtil';
import ProfileAvatar from '../../presentation/my-page/components/ProfileAvatar';

const AddUserProfileV1BottomSheet = ({
  title,
  userProfile,
  isShowProfileBottomSheet,
  setIsShowProfileBottomSheet,
  isMergeProfile = false,
  category = 'trading',
  afterGoBack = false,
  disableDrag = true,
  disableBackdrop = false,
  onCompleted
}) => {
  const containerRef = useRef();
  const inputRef = useRef();
  const navigate = useNavigate();
  const successPhotoCardToast = useSuccessPhotoCardToast();
  const { mutateAsync: addAndMergeUserProfile } = useAddAndMergeUserProfile();
  const { mutateAsync: addUserProfile } = useAddUserProfile();

  const [nickname, setNickname] = useState(userProfile?.nickname);
  const [errorMessage, setErrorMessage] = useState('');

  const { openFilePicker, filesContent } = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: false
  });

  const buttonText = () => {
    if (!nickname) return t('profiles.defaultButton');
    return hasBatchim(nickname)
      ? t('profiles.hasBatchimNickNameButton', { nickname: nickname })
      : t('profiles.nickNameButton', { nickname: nickname });
  };

  const onFocusedInput = () => {
    if (isNativeIOS()) return;
    if (containerRef.current && isMobile && isIOS) {
      containerRef.current.style.paddingBottom = '360px';
    }
  };

  const onUnFocusedInput = () => {
    if (isNativeIOS()) return;
    if (containerRef.current && isMobile && isIOS) {
      containerRef.current.style.paddingBottom = '0px';
    }
  };

  const listener = () => {
    const MIN_KEYBOARD_HEIGHT = 300; // N.B.! this might not always be correct
    const isMobile = window.innerWidth < 768;
    const isKeyboardOpen =
      isMobile &&
      isIOS &&
      window.screen.height - MIN_KEYBOARD_HEIGHT > window.visualViewport.height;

    if (!isKeyboardOpen && inputRef.current) {
      onUnFocusedInput();
    }
  };
  window.visualViewport.addEventListener('resize', listener);

  const onUpdateNickname = (nickname) => {
    setErrorMessage('');
    setNickname(nickname);
  };

  const createUserProfile = async (nickname) => {
    if (!nickname) {
      return setErrorMessage(t('profiles.emptyNicknameErrorMessage'));
    }
    if (userProfile && !isMergeProfile) {
      category = userProfile.category;
    }
    const requestBody = {
      user_profile: {
        category: category,
        nickname: nickname,
        data_url: filesContent[0]?.content
      }
    };
    if (isMergeProfile) {
      const response = await addAndMergeUserProfile(requestBody);

      if (response?.tradingUserProfiles) {
        successPhotoCardToast('bottom', t('profiles.mergedNicknameToastMessage'));
        completed();
      }
    } else {
      const response = await addUserProfile(requestBody);
      if (response?.userProfile) {
        completed();
      }
    }
  };

  const completed = () => {
    setIsShowProfileBottomSheet(false);
    if (onCompleted) {
      onCompleted();
    }
    if (afterGoBack) {
      navigate(-1);
    }
  };

  return (
    <Sheet
      isOpen={isShowProfileBottomSheet}
      disableDrag={disableDrag}
      onClose={() => setIsShowProfileBottomSheet(false)}
      detent="content"
    >
      <Sheet.Container className="!bg-background-elevated !rounded-t-[20px] !pb-[env(safe-area-inset-bottom)] !shadow-none">
        <Sheet.Header className="bottomSheetHeader" style={{ paddingTop: '30px' }}>
          <div className="text-center">
            <p className="text-[17px] font-bold leading-[20px] word-break-keep-all text-pure-black dark:text-pure-white">
              {title || t('profiles.title')}
            </p>
          </div>
        </Sheet.Header>
        <Sheet.Content ref={containerRef} disableDrag={true} className="sticky px-[24px]">
          <div className="flex justify-center">
            <button type="button" onClick={openFilePicker} className="relative">
              <ProfileAvatar
                widthAndHeight="64px"
                svgWidthAndHeight="45px"
                thumbnailUrl={first(filesContent)?.content || userProfile?.thumbnailUrl || ''}
              />
              <div className="absolute bottom-[6px] right-[-2px] bg-text-primary border-[2px] border-pure-white dark:border-background-elevated rounded-full p-[3px]">
                <EditFill size={10} className="text-pure-white" />
              </div>
            </button>
          </div>
          <div className="mt-[16px] flex flex-col items-end">
            <InputBoxSingle
              ref={inputRef}
              _focusVisible={true}
              placeholder={t('profiles.inputNickNamePlaceholder')}
              maxLength={20}
              value={nickname}
              onFocus={() => onFocusedInput()}
              onBlur={() => onUnFocusedInput()}
              onTouchStart={(e) =>
                setTimeout(() => {
                  onFocusedInput(e);
                }, 150)
              }
              onChange={(e) => onUpdateNickname(e.target.value)}
              showInputLength={true}
              message={errorMessage || '\u00A0'}
              messageType={errorMessage ? 'error' : 'normal'}
              className="bg-transparent border-none"
            />
          </div>
          <Button
            size="fill"
            disabled={!nickname && !errorMessage}
            onClick={() => createUserProfile(nickname)}
            className="rounded-[100px] text-[17px] font-semibold mt-[12px]"
          >
            {buttonText()}
          </Button>
        </Sheet.Content>
      </Sheet.Container>
      <Sheet.Backdrop onClick={() => !disableBackdrop && setIsShowProfileBottomSheet(false)} />
    </Sheet>
  );
};

export default AddUserProfileV1BottomSheet;